import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, retry, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';

/**
 * Global HTTP Error Interceptor.
 *
 * @description
 * Intercepts all outgoing HTTP requests and incoming HTTP responses.
 * Handles retry logic and global error notifications using Toastr.
 *
 * Responsibilities:
 * - Retry failed HTTP requests (up to defined count)
 * - Catch HTTP errors globally
 * - Display user-friendly error messages
 * - Rethrow error for optional component-level handling
 *
 * @implements {HttpInterceptor}
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  /**
   * Creates an instance of ErrorInterceptor.
   *
   * @param {ToastrService} toastrService - Service used for displaying toast notifications.
   */
  constructor(private toastrService: ToastrService) { }

  /**
   * Intercepts HTTP requests and handles errors globally.
   *
   * @param {HttpRequest<any>} req - The outgoing HTTP request.
   * @param {HttpHandler} next - The next interceptor in the chain.
   * @returns {Observable<HttpEvent<any>>} An observable stream of HTTP events.
   *
   * @description
   * - Retries the request up to 3 times before failing.
   * - Displays an error notification using Toastr if request fails.
   * - Rethrows the error to allow further handling in components if required.
   */
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    return next.handle(req).pipe(

      /**
       * Retry failed requests up to 3 times.
       * Useful for transient network/server issues.
       */
      retry(1),

      /**
       * Catch HTTP errors and handle them globally.
       */
      catchError((error: HttpErrorResponse) => {

        /**
         * Display error message using Toastr notification.
         * error.message provides a generic Angular HTTP error message.
         */
        // this.toastrService.error(error.message, 'Error');

        /**
         * Rethrow the original error so that
         * components can optionally handle it.
         */
        return throwError(() => error);
      })
    );
  }
}